import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems } from '../support/queries';

export type SfTeamOpportunitiesPovLeanFullResponse =
  | Record<string, { opportunities?: any[]; reports?: any }>
  | any;

export type PovSummaryCounts = {
  opp_count: number;
  opp_with_pov_count: number;
  pov_count: number;
  outcome_count: number;
  usecase_count: number;
  test_case_count: number;
  metric_count: number;
};

export type SfTeamOpportunitiesPovSummaryNode = {
  email: string;
  is_manager: boolean;
  own?: PovSummaryCounts;
  rollup?: PovSummaryCounts;
  reports?: Record<string, SfTeamOpportunitiesPovSummaryNode>;
};

export type SfTeamOpportunitiesPovLeanSummaryResponse = {
  manager_email: string;
  is_manager: boolean;
  view: 'summary';
  self: SfTeamOpportunitiesPovSummaryNode;
  reports: Record<string, SfTeamOpportunitiesPovSummaryNode>;
};

export type SfTeamOpportunitiesPovLeanResponse =
  | SfTeamOpportunitiesPovLeanFullResponse
  | SfTeamOpportunitiesPovLeanSummaryResponse
  | any;

export type OpportunityPovDetailResponse = {
  opportunity_id: string;
  has_pov: boolean;
  povs: any[];
};

export interface UseSfTeamOpportunitiesPovLeanOptions {
  enabled?: boolean;
  maxDepth?: number;
  view?: 'full' | 'summary';
  /**
   * The Leader dashboard's `current_time_filter` (`all`, or `range:YYYY-MM-DD:YYYY-MM-DD` for a fiscal
   * month / quarter / year). The server keeps only opportunities whose Close Date falls in the window.
   */
  currentTimeFilter?: string;
  [key: string]: any;
}

export function useSfTeamOpportunitiesPovLean(
  email: string | null | undefined,
  options?: UseSfTeamOpportunitiesPovLeanOptions
) {
  const normalizedEmail = String(email || '').toLowerCase() || null;
  const maxDepth = options?.maxDepth ?? 5;
  const view = options?.view || 'full';
  const timeFilter = options?.currentTimeFilter || 'all';

  const queryOptions = { ...(options || {}) };
  delete queryOptions.maxDepth;
  delete queryOptions.view;
  delete queryOptions.currentTimeFilter;

  return useQuery({
    queryKey: ['SfTeamOpportunitiesPovLean', normalizedEmail, maxDepth, view, timeFilter],
    queryFn: async () => {
      if (!normalizedEmail) return null;

      return fetchItems(
        'sf/team/opportunities-pov-lean',
        {
          email: normalizedEmail,
          max_depth: maxDepth,
          view,
          ...(timeFilter !== 'all' ? { current_time_filter: timeFilter } : {}),
        },
        { includePageSize: false }
      ) as Promise<SfTeamOpportunitiesPovLeanResponse>;
    },
    ...{
        enabled: !!normalizedEmail && ((options?.enabled ?? true) as boolean),
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        ...queryOptions,
      },
  });
}

export function useOpportunityPovDetail(
  opportunityId: string | null | undefined,
  options?: any
) {
  const normalizedOpportunityId = String(opportunityId || '') || null;

  return useQuery({
    queryKey: ['OpportunityPovDetail', normalizedOpportunityId],
    queryFn: async () => {
      if (!normalizedOpportunityId) return null;

      return fetchItems(
        `sf/opportunity/${normalizedOpportunityId}/pov`,
        {},
        { includePageSize: false }
      ) as Promise<OpportunityPovDetailResponse>;
    },
    ...{
        enabled: !!normalizedOpportunityId && ((options?.enabled ?? true) as boolean),
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        ...(options || {}),
      },
  });
}